import { useState } from 'react';

const useFormState = () => {
  // Page 1: Personal Info
  const [personalInfo, setPersonalInfo] = useState({
    name: '',
    email: '',
    hospital: '',
    role: ''
  });

  // Page 2: NPS
  const [npsScore, setNpsScore] = useState(null);
  const [npsFeedback, setNpsFeedback] = useState('');

  // Page 3: Multiple Select Benefits
  const [selectedBenefits, setSelectedBenefits] = useState([]);
  const [otherBenefit, setOtherBenefit] = useState('');

  // Page 4: Agreement Questions
  const [agreementResponses, setAgreementResponses] = useState({
    question1: {
      option1: false,
      option2: false,
      option3: false,
      option4: false
    },
    question2: {
      option1: false,
      option2: false,
      option3: false,
      option4: false
    }
  });

  // Page 5: Frontline Critical Care
  const [frontlineCareResponse, setFrontlineCareResponse] = useState('');

  // Page 6: Rating Questions
  const [treatmentRatings, setTreatmentRatings] = useState({
    treatmentPlan: null,      // CP physician's team
    healthImprovement: null,  // Patient health improvement
    hourlyMonitoring: null    // CP's hourly monitoring
  });

  // Page 7: Feedback Questions
  const [serviceFeedback, setServiceFeedback] = useState({
    physicianConsult: '',     // CP's offering (Physician Consult)
    nursingServices: '',      // CP's offering (Nursing services)
    concerns: ''             // Issues/Concerns
  });

  // Page 8: Communication Ratings
  const [communicationRatings, setCommunicationRatings] = useState({
    icu: null,
    cst: null
  });

  // Page 9: ER Services Awareness
  const [erAwareness, setErAwareness] = useState(null); // yes/no

  // Page 10: ER Services Rating and Reasons
  const [erRating, setErRating] = useState(null);
  const [ratingReason, setRatingReason] = useState('');

  // Page 11: Monthly Invoice (combined with feedback)
  const [invoiceClarity, setInvoiceClarity] = useState(null);
  const [invoiceFeedback, setInvoiceFeedback] = useState('');

  // Page 12: Product Feedback
  const [productFeedback, setProductFeedback] = useState({
    digitization: {
      option1: false, // Strongly Agree
      option2: false, // Agree
      option3: false, // Disagree
      option4: false  // Strongly Disagree
    },
    radarFeatures: ''
  });

  const [radarUsage, setRadarUsage] = useState('');

  // Form submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Handler functions
  const updatePersonalInfo = (field, value) => {
    setPersonalInfo(prev => ({ ...prev, [field]: value }));
  };

  const updateTreatmentRating = (field, value) => {
    setTreatmentRatings(prev => ({ ...prev, [field]: value }));
  };

  const updateServiceFeedback = (field, value) => {
    setServiceFeedback(prev => ({ ...prev, [field]: value }));
  };

  const updateCommunicationRating = (field, value) => {
    setCommunicationRatings(prev => ({ ...prev, [field]: value }));
  };

  const toggleBenefit = (benefit) => {
    setSelectedBenefits(prev => {
      if (prev.includes(benefit)) {
        return prev.filter(item => item !== benefit);
      }
      return [...prev, benefit];
    });
  };

  const updateAgreementResponse = (question, option) => {
    setAgreementResponses(prev => ({
      ...prev,
      [question]: {
        option1: option === 'option1',
        option2: option === 'option2',
        option3: option === 'option3',
        option4: option === 'option4'
      }
    }));
  };

  const updateProductFeedback = (newFeedback) => {
    setProductFeedback(newFeedback);
  };

  const resetForm = () => {
    setPersonalInfo({ name: '', email: '', hospital: '', role: '' });
    setNpsScore(null);
    setNpsFeedback('');
    setSelectedBenefits([]);
    setOtherBenefit('');
    setAgreementResponses({
      question1: { option1: false, option2: false, option3: false, option4: false },
      question2: { option1: false, option2: false, option3: false, option4: false }
    });
    setFrontlineCareResponse('');
    setTreatmentRatings({ treatmentPlan: null, healthImprovement: null, hourlyMonitoring: null });
    setServiceFeedback({ physicianConsult: '', nursingServices: '', concerns: '' });
    setCommunicationRatings({ icu: null, cst: null });
    setErAwareness(null);
    setErRating(null);
    setRatingReason('');
    setInvoiceClarity(null);
    setInvoiceFeedback('');
    setProductFeedback({
      digitization: {
        option1: false,
        option2: false,
        option3: false,
        option4: false
      },
      radarFeatures: ''
    });
    setRadarUsage('');
    setError(null);
    setIsSubmitting(false);
  };

  return {
    // State
    personalInfo,
    npsScore,
    npsFeedback,
    selectedBenefits,
    otherBenefit,
    agreementResponses,
    frontlineCareResponse,
    treatmentRatings,
    serviceFeedback,
    communicationRatings,
    erAwareness,
    erRating,
    ratingReason,
    invoiceClarity,
    invoiceFeedback,
    productFeedback,
    updateProductFeedback,
    radarUsage,
    setRadarUsage,
    isSubmitting,
    error,

    // Setters
    setPersonalInfo,
    setNpsScore,
    setNpsFeedback,
    setOtherBenefit,
    setFrontlineCareResponse,
    setErAwareness,
    setErRating,
    setRatingReason,
    setInvoiceClarity,
    setInvoiceFeedback,
    setIsSubmitting,
    setError,

    // Handler functions
    updatePersonalInfo,
    updateTreatmentRating,
    updateServiceFeedback,
    updateCommunicationRating,
    toggleBenefit,
    updateAgreementResponse,
    resetForm
  };
};

export default useFormState;